import React, {useEffect, useState} from 'react';
import {Button, ColumnDataType, DataTable, Spinner} from "@tag/tag-components-react-v4";
import {AppGroup} from "../types/AppGroup";
import {ServiceHealthDTO} from "../types/ServiceHealthDTO";
import {CrossFilled, MinusLined, TickFilled, DownloadFilled} from "@tag/tag-icons";
import { ProjectAccents } from "../../General/config/projectAccents";

const ServiceHealth = (props: {selectedAppGroupHealthSummary: {appGrp: AppGroup, serviceHealths: ServiceHealthDTO[]}}) => {
    
    const [loading, setLoading] = useState<boolean>();

    const getColumns = () => {
        return [
            { accessorKey: 'serviceDns', header: 'Service Name', meta: { dataType: 'string' as ColumnDataType } },
            { accessorKey: 'clientId', header: 'Client ID', meta: { dataType: 'number' as ColumnDataType } },
            { accessorKey: 'companyName', header: 'Customer', meta: { dataType: 'string' as ColumnDataType } },
            { accessorKey: 'ping', header: 'Ping', cell: (e: any) => renderSuccessOrFailImage(e.row.original.ping) },
            { accessorKey: 'workspaceHealth', header: 'Workspace API', cell: (e: any) => renderSuccessOrFailImage(e.row.original.workspaceHealth) },
            { accessorKey: 'datahubHealth', header: 'Datahub API', cell: (e: any) => renderSuccessOrFailImage(e.row.original.datahubHealth) },
            { accessorKey: 'corporateSiteHealth', header: 'CorporateSite API', cell: (e: any) => renderSuccessOrFailImage(e.row.original.corporateSiteHealth) },
        ];
    }
    
    const getData = () => {
        return props.selectedAppGroupHealthSummary.serviceHealths?.map(s => {
            return {
                serviceDns: s.serviceDns,
                clientId: s.clientId,
                companyName: s.companyName,
                ping: s.servicePing,
                workspaceHealth: s.workspaceApi,
                datahubHealth: s.datahubApi,
                corporateSiteHealth: s.corporateSiteApi,
                shade: getShade(s.companyName, s.servicePing, s.workspaceApi, s.datahubApi, s.corporateSiteApi)
            }
        });
    }

    const exportToExcel = () => {
        const data = getData();
        if (!data || data.length === 0) return;

        const headers = ['Service Name', 'Client ID', 'Customer', 'Ping', 'Workspace API', 'Datahub API', 'CorporateSite API'];
        const csvContent = [
            headers.join(','),
            ...data.map(row => [
                `"${row.serviceDns || ''}"`,
                row.clientId ?? '',
                `"${row.companyName || ''}"`,
                row.ping === null || row.ping === undefined ? '' : row.ping ? 'Yes' : 'No',
                row.workspaceHealth === null || row.workspaceHealth === undefined ? '' : row.workspaceHealth ? 'Yes' : 'No',
                row.datahubHealth === null || row.datahubHealth === undefined ? '' : row.datahubHealth ? 'Yes' : 'No',
                row.corporateSiteHealth === null || row.corporateSiteHealth === undefined ? '' : row.corporateSiteHealth ? 'Yes' : 'No'
            ].join(','))
        ].join('\n');

        const blob = new Blob(['\ufeff' + csvContent], { type: 'application/vnd.ms-excel;charset=utf-8' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${props.selectedAppGroupHealthSummary.appGrp.name}_ServiceHealth.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }
    
    const renderSuccessOrFailImage = (val?: boolean) => {
        if (val === null || val === undefined) {
            return <MinusLined accent={ProjectAccents.CareCompliance} />;
        }
        
        if (val)
            return <TickFilled accent={ProjectAccents.PeoplePlanner} />
        else
            return <CrossFilled accent={ProjectAccents.PoliciesAndProcedures} />
    }
    
    const getShade = (company: string, ping?: boolean, workspaceApi?: boolean, datahubApi?: boolean, corpApi?: boolean) => {
        // If unassigned service, white background
        if (!company) {
            return '#FFF';
        }
        
        // If assigned and all success, green background
        if (ping && workspaceApi && datahubApi && corpApi) {
            return '#A7D9B9';
        }
        
        // Otherwise red as somewhere there is an error
        return '#F39F97';
    }

    let content = null;
    if (loading) {
        content = <Spinner />
    }
    else {
        content = (
            <>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1em', marginRight: '1em' }}>
                    <Button
                        onClick={exportToExcel}
                        icon={<DownloadFilled />}
                    >
                        Export to Excel
                    </Button>
                </div>
                <DataTable
                    scrollerProps={{
                        style:{ width: 'unset' } // Temporary while scroller not needed
                    }}
                    getRowProps={(row) => ({
                        style: {
                            backgroundColor: row.original.shade
                        },
                    })}
                    columns={getColumns()}
                    data={getData()}
                />
            </>
        );
    }

    return (
        <>
            {content}
        </>
    );
}

export default ServiceHealth;
